import { useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router';
import { IconButton } from '@design-system/components/button/iconButton';
import { ButtonProps } from '@design-system/components/button/button';

export type BasicMenuItem = {
  label: string;
  onClick?: () => void;
  url?: string;
  icon?: React.ReactNode;
  testId?: string;
};

export type BasicMenuProps = {
  items: BasicMenuItem[];
  title: string;
  disabled?: boolean;
  styles?: {
    button?: string;
    menu?: string;
    menuItem?: string;
  };
  testId?: string;
  buttonColor?: ButtonProps['color'];
};

const toMenuItem = (
  item: BasicMenuItem,
  onItemClick: (callback?: () => void) => void,
) => {
  if (item?.url) {
    return (
      <MenuItem
        data-testid={item.testId}
        key={item.label}
        className="min-w-[150px] p-0"
      >
        <Link to={item.url} className="px-4 py-1.5 w-full">
          <Typography className="text-black flex gap-2">
            {item.icon}
            {item.label}
          </Typography>
        </Link>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      data-testid={item.testId}
      key={item.label}
      onClick={() => onItemClick(item.onClick)}
      className="min-w-[150px]"
    >
      {item.icon ? (
        <Typography className="text-black flex gap-2">
          {item.icon}
          {item.label}
        </Typography>
      ) : (
        item.label
      )}
    </MenuItem>
  );
};

export const BasicMenu = ({
  items,
  title,
  disabled,
  testId,
  buttonColor = 'neutral',
}: BasicMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((state) => (state === null ? event.currentTarget : null));
  };

  const onItemClick = (callback?: () => void) => {
    setAnchorEl(null);
    callback && callback();
  };

  return (
    <>
      <IconButton
        data-testid={testId}
        onClick={handleClick}
        variant="filled"
        className="px-3"
        color={buttonColor}
        disabled={disabled}
        icon={faPlus}
      >
        {title}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClick}
        className="mt-3"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {items.map((item) => toMenuItem(item, onItemClick))}
      </Menu>
    </>
  );
};
